import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { STATUS_COLORS } from '../mock';
import { useToast } from '../hooks/use-toast';
import { Search, ShieldCheck, FileText, Archive, Trash2, RotateCcw, X, Download, FilePlus, Upload as UploadIcon, Sparkles, Loader2, ZoomIn, ZoomOut, Maximize2, ExternalLink } from 'lucide-react';
import EmailSettings from '../components/EmailSettings';
import EmailLogs from '../components/EmailLogs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ['Alle', 'Ontvangen', 'In behandeling', 'Ingediend', 'Beslissing', 'Gewonnen', 'Afgewezen'];
const STATUS_OPTIONS = ['Ontvangen', 'In behandeling', 'Ingediend', 'Beslissing', 'Gewonnen', 'Afgewezen'];
const TYPES = ['Alle', 'CJIB-verkeersboete (Mulder)', 'Naheffingsaanslag parkeerbelasting', 'Strafbeschikking', 'Aanmaning/dwangbevel'];

// ---- DocumentPane component ----
const getExt = (name) => (name || '').toLowerCase().split('.').pop();
const isImage = (name) => ['jpg','jpeg','png','gif','webp','heic','heif','bmp','svg'].includes(getExt(name));
const isPdf = (name) => getExt(name) === 'pdf';
const isOffice = (name) => ['docx','doc','xlsx','xls','pptx','ppt'].includes(getExt(name));

const DocumentPane = ({ title, fileId, fileName, apiBase, emptyText, titleAsFilename }) => {
  const [zoom, setZoom] = React.useState(1);
  const [imgError, setImgError] = React.useState(false);
  const url = fileId ? `${apiBase}/files/${fileId}` : null;
  const downloadUrl = fileId ? `${apiBase}/files/${fileId}?download=1` : null;
  const downloadName = fileName || 'bestand';

  React.useEffect(() => { setZoom(1); setImgError(false); }, [fileId]);

  const zoomIn = () => setZoom(z => Math.min(z + 0.25, 3));
  const zoomOut = () => setZoom(z => Math.max(z - 0.25, 0.4));
  const fit = () => setZoom(1);

  const image = isImage(fileName);
  const pdf = isPdf(fileName);
  const office = isOffice(fileName);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white flex flex-col" data-testid="document-pane">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200 gap-2">
        <div className="text-xs font-semibold text-[#1e1b4b] uppercase tracking-wide truncate" title={titleAsFilename ? fileName : title}>
          {titleAsFilename ? (fileName || title) : title}
        </div>
        {fileId && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {image && (
              <>
                <button onClick={zoomOut} className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Uitzoomen" data-testid="doc-zoom-out"><ZoomOut className="w-3.5 h-3.5" /></button>
                <span className="text-xs text-slate-600 font-mono w-10 text-center">{Math.round(zoom * 100)}%</span>
                <button onClick={zoomIn} className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Inzoomen" data-testid="doc-zoom-in"><ZoomIn className="w-3.5 h-3.5" /></button>
                <button onClick={fit} className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Passend maken" data-testid="doc-zoom-fit"><Maximize2 className="w-3.5 h-3.5" /></button>
              </>
            )}
            <a href={url} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Openen in nieuw tabblad" data-testid="doc-open"><ExternalLink className="w-3.5 h-3.5" /></a>
            <a href={downloadUrl} download={downloadName} className="p-1.5 rounded hover:bg-slate-200 text-slate-700" title="Downloaden" data-testid="doc-download"><Download className="w-3.5 h-3.5" /></a>
          </div>
        )}
      </div>
      <div className="relative bg-slate-100 overflow-auto" style={{ height: '480px' }}>
        {!fileId && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 text-sm">
            <FileText className="w-8 h-8 mb-2 opacity-40" />
            {emptyText || 'Geen document'}
          </div>
        )}
        {fileId && image && !imgError && (
          <div style={{ transform: `scale(${zoom})`, transformOrigin: 'top left', width: `${100 / zoom}%`, height: zoom > 1 ? `${100 * zoom}%` : '100%' }} className="min-h-full">
            <img
              src={url}
              alt={fileName || 'document'}
              className="w-full h-full object-contain bg-white"
              onError={() => setImgError(true)}
              data-testid="doc-image-preview"
            />
          </div>
        )}
        {fileId && pdf && (
          <iframe title={fileName || 'pdf'} src={url} className="w-full h-full border-0 bg-white" data-testid="doc-pdf-preview" />
        )}
        {fileId && (office || (!image && !pdf) || (image && imgError)) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-600 text-sm p-6 text-center gap-3">
            <FileText className="w-10 h-10 text-[#1e1b4b] opacity-70" />
            <div className="font-medium text-slate-800">Voorbeeld niet beschikbaar</div>
            <div className="text-xs text-slate-500 max-w-xs break-all">{fileName || fileId}</div>
            <div className="flex gap-2 mt-2">
              <a href={url} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" data-testid="doc-open-btn"><ExternalLink className="w-3.5 h-3.5 mr-2" /> Openen</Button>
              </a>
              <a href={downloadUrl} download={downloadName}>
                <Button size="sm" className="bg-[#1e1b4b] hover:bg-[#2a2464] text-white" data-testid="doc-download-btn"><Download className="w-3.5 h-3.5 mr-2" /> Downloaden</Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const { toast } = useToast();
  const [tab, setTab] = useState('active');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('Alle');
  const [typeFilter, setTypeFilter] = useState('Alle');
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [generating, setGenerating] = useState(null); // 'machtiging' | 'bezwaar' | null
  const [uploadingTemplate, setUploadingTemplate] = useState(null); // 'machtiging' | 'bezwaar' | null

  useEffect(() => {
    if (!user) nav('/login');
    else if (user.role !== 'admin') nav('/');
  }, [user, nav]);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/templates`);
      setTemplates(res.data);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { if (user?.role === 'admin') loadTemplates(); }, [user]);

  const templateFor = (type) => templates.find(t => t.type === type);

  const load = async (view = tab) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/submissions?view=${view}`);
      setItems(res.data);
    } catch (e) {
      toast({ title: 'Kon aanmeldingen niet laden', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.role === 'admin') load(tab); /* eslint-disable-next-line */ }, [tab, user]);

  const filtered = useMemo(() => items.filter(s =>
    (statusFilter === 'Alle' || s.status === statusFilter) &&
    (typeFilter === 'Alle' || s.boete?.type === typeFilter) &&
    (!q ||
      (s.kenmerk || '').toLowerCase().includes(q.toLowerCase()) ||
      (s.client?.naam || '').toLowerCase().includes(q.toLowerCase()) ||
      (s.client?.email || '').toLowerCase().includes(q.toLowerCase()) ||
      (s.boete?.kenteken || '').toLowerCase().includes(q.toLowerCase())
    )
  ), [items, q, statusFilter, typeFilter]);

  const patch = async (id, changes) => {
    try {
      await axios.patch(`${API}/submissions/${id}`, changes);
      toast({ title: 'Bijgewerkt' });
      await load(tab);
      if (selected && selected.id === id) {
        setSelected(prev => ({ ...prev, ...changes }));
      }
    } catch (e) {
      toast({ title: 'Bijwerken mislukt', description: e.message, variant: 'destructive' });
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/submissions/${id}`);
      setSelected(res.data);
      setDetailOpen(true);
    } catch (e) {
      toast({ title: 'Details laden mislukt', description: e.message, variant: 'destructive' });
    }
  };

  const uploadTemplate = async (type, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast({ title: 'Alleen .docx-bestanden', variant: 'destructive' });
      return;
    }
    setUploadingTemplate(type);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('file', file);
      await axios.post(`${API}/templates/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast({ title: 'Sjabloon opgeslagen', description: `${type === 'machtiging' ? 'Machtiging' : 'Pro-forma bezwaar'}-sjabloon geactiveerd.` });
      await loadTemplates();
    } catch (e) {
      toast({ title: 'Uploaden mislukt', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setUploadingTemplate(null);
    }
  };

  const generateDocument = async (type) => {
    if (!selected) return;
    setGenerating(type);
    try {
      const res = await axios.post(`${API}/submissions/${selected.id}/generate?type=${type}`);
      toast({ title: 'Document gegenereerd', description: res.data.docx_file_name });
      // refresh selected
      const full = await axios.get(`${API}/submissions/${selected.id}`);
      setSelected(full.data);
    } catch (e) {
      toast({ title: 'Genereren mislukt', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setGenerating(null);
    }
  };

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="hero-gradient text-white py-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 text-slate-300 text-sm"><ShieldCheck className="w-4 h-4" /> Admin - Juristendashboard</div>
          <h1 className="text-3xl md:text-4xl font-bold mt-1">Aanmeldingen</h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList>
            <TabsTrigger value="active" data-testid="tab-active">Actief</TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">Gearchiveerd</TabsTrigger>
            <TabsTrigger value="deleted" data-testid="tab-deleted">Verwijderd</TabsTrigger>
            <TabsTrigger value="email_settings" data-testid="tab-email-settings">E-mailinstellingen</TabsTrigger>
            <TabsTrigger value="email_logs" data-testid="tab-email-logs">E-maillogboek</TabsTrigger>
          </TabsList>

          <TabsContent value="email_settings" className="mt-4"><EmailSettings /></TabsContent>
          <TabsContent value="email_logs" className="mt-4"><EmailLogs /></TabsContent>

          {['active', 'archived', 'deleted'].map(v => (
            <TabsContent key={v} value={v} className="mt-4">
              <Card className="p-4 md:p-5 border border-slate-200 mb-4">
                <div className="grid md:grid-cols-4 gap-3">
                  <div className="relative md:col-span-2">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek op kenmerk, klant, e-mail of kenteken..." className="pl-9" />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={typeFilter} onValueChange={setTypeFilter}>
                    <SelectTrigger><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </Card>

              <Card className="border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-slate-600">
                        <th className="px-4 py-3 font-semibold">Kenmerk</th>
                        <th className="px-4 py-3 font-semibold">Klant</th>
                        <th className="px-4 py-3 font-semibold">Type</th>
                        <th className="px-4 py-3 font-semibold">Instantie</th>
                        <th className="px-4 py-3 font-semibold">Deadline</th>
                        <th className="px-4 py-3 font-semibold text-right">Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Laden...</td></tr>}
                      {!loading && filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Geen aanmeldingen in deze weergave.</td></tr>}
                      {filtered.map(s => (
                        <tr key={s.id} onClick={() => openDetail(s.id)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                          <td className="px-4 py-3 font-mono text-[#1e1b4b] font-semibold">{s.kenmerk}</td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-800">{s.client?.naam || '-'}</div>
                            <div className="text-xs text-slate-500">{s.client?.email || '-'}</div>
                          </td>
                          <td className="px-4 py-3 text-slate-700">{s.boete?.type || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{s.boete?.instantie || '-'}</td>
                          <td className="px-4 py-3 text-slate-700">{s.boete?.uiterlijke_datum_bezwaar || '-'}</td>
                          <td className="px-4 py-3 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                            {v === 'active' && (
                              <>
                                <Button size="sm" variant="ghost" title="Archiveren" onClick={() => patch(s.id, { archived: true })}><Archive className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" title="Verwijderen" onClick={() => patch(s.id, { deleted: true })}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                              </>
                            )}
                            {v === 'archived' && (
                              <>
                                <Button size="sm" variant="ghost" title="Herstellen" onClick={() => patch(s.id, { archived: false })}><RotateCcw className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" title="Verwijderen" onClick={() => patch(s.id, { deleted: true })}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                              </>
                            )}
                            {v === 'deleted' && (
                              <Button size="sm" variant="ghost" title="Herstellen" onClick={() => patch(s.id, { deleted: false })}><RotateCcw className="w-4 h-4" /></Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span className="font-mono text-[#1e1b4b]">{selected?.kenmerk}</span>
              {selected && <Badge className={(STATUS_COLORS[selected.status] || 'bg-slate-100 text-slate-800 border-slate-200') + ' border'}>{selected.status}</Badge>}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-6">
              {/* Status & notes */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase text-slate-500 tracking-wider">Status wijzigen</Label>
                  <Select value={selected.status} onValueChange={v => patch(selected.id, { status: v })}>
                    <SelectTrigger className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500 tracking-wider">Aangemeld op</Label>
                  <div className="text-sm text-slate-700 mt-2">{new Date(selected.created_at).toLocaleString('nl-NL')}</div>
                </div>
              </div>

              {/* Boete details */}
              <div>
                <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">Boetegegevens (uitgelezen)</div>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    ['Beschikkingsnummer', selected.boete?.beschikkingsnummer],
                    ['Datum beschikking', selected.boete?.datum_beschikking],
                    ['Instantie', selected.boete?.instantie],
                    ['Type', selected.boete?.type],
                    ['Kenteken', selected.boete?.kenteken],
                    ['Voertuigsoort', selected.boete?.voertuigsoort],
                    ['Bedrag', selected.boete?.bedrag != null ? `€ ${Number(selected.boete.bedrag).toFixed(2)}` : null],
                    ['Uiterlijke datum bezwaar', selected.boete?.uiterlijke_datum_bezwaar],
                    ['Tijdstip gedraging', selected.boete?.tijdstip_gedraging],
                    ['Locatie gedraging', selected.boete?.locatie_gedraging],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-1.5">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-medium text-slate-800 text-right">{v || '-'}</span>
                    </div>
                  ))}
                </div>
                {selected.boete?.omschrijving_overtreding && (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                    <div className="text-xs text-slate-500 mb-1">Omschrijving overtreding</div>
                    <div className="text-slate-800">{selected.boete.omschrijving_overtreding}</div>
                  </div>
                )}
              </div>

              {/* NAW gegevens */}
              <div>
                <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">NAW-gegevens (zoals op boete)</div>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    ['Naam', selected.boete?.naam_geadresseerde],
                    ['Straat', selected.boete?.straat_geadresseerde],
                    ['Postcode', selected.boete?.postcode_geadresseerde],
                    ['Woonplaats', selected.boete?.woonplaats_geadresseerde],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-1.5">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-medium text-slate-800 text-right">{v || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Client */}
              <div>
                <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">Opgegeven contactgegevens</div>
                <div className="grid md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    ['Aanhef', selected.client?.aanhef],
                    ['Naam', selected.client?.naam],
                    ['E-mail', selected.client?.email],
                    ['Telefoon', selected.client?.telefoon],
                    ['Straat', selected.client?.straat],
                    ['Postcode', selected.client?.postcode],
                    ['Woonplaats', selected.client?.woonplaats],
                    ['Naam op boete', selected.client?.naam_op_boete],
                    ['Bedrijf', selected.client?.bedrijf],
                    ['Leasemaatschappij', selected.client?.lease],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-slate-100 py-1.5">
                      <span className="text-slate-500">{k}</span>
                      <span className="font-medium text-slate-800 text-right">{v || '-'}</span>
                    </div>
                  ))}
                </div>
                {selected.client?.reden && (
                  <div className="mt-3 bg-slate-50 border border-slate-200 rounded p-3 text-sm">
                    <div className="text-xs text-slate-500 mb-1">Reden bezwaar (klant)</div>
                    <div className="text-slate-800 whitespace-pre-wrap">{selected.client.reden}</div>
                  </div>
                )}
              </div>

              {/* Handtekening */}
              {selected.client?.handtekening && (
                <div>
                  <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">Handtekening</div>
                  <img src={selected.client.handtekening} alt="handtekening" className="border border-slate-200 rounded-md bg-white max-w-md" />
                </div>
              )}

              {/* Documenten preview - 2 kolommen */}
              {(selected.file_id || (selected.additional_file_ids && selected.additional_file_ids.length > 0)) && (
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Linker kolom: hoofddocument (beschikking) */}
                  <DocumentPane
                    title="Geüpload document (beschikking)"
                    fileId={selected.file_id}
                    fileName={selected.file_name}
                    apiBase={API}
                  />

                  {/* Rechter kolom: eerste aanvullende document */}
                  <DocumentPane
                    title="Aanvullende documenten / foto's"
                    fileId={selected.additional_file_ids?.[0]}
                    fileName={selected.additional_file_names?.[0]}
                    apiBase={API}
                    emptyText="Geen aanvullende documenten"
                  />
                </div>
              )}

              {/* Extra aanvullende documenten (2e en verder) onder de rechterkolom */}
              {selected.additional_file_ids && selected.additional_file_ids.length > 1 && (
                <div>
                  <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">Overige aanvullende documenten</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    {selected.additional_file_ids.slice(1).map((fid, i) => (
                      <DocumentPane
                        key={fid}
                        title={selected.additional_file_names?.[i + 1] || `Bestand ${i + 2}`}
                        fileId={fid}
                        fileName={selected.additional_file_names?.[i + 1]}
                        apiBase={API}
                        titleAsFilename
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Authorization file */}
              {selected.authorization_file_id && (
                <div>
                  <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">Machtiging / contract (bedrijf of lease)</div>
                  <DocumentPane
                    title={selected.authorization_file_name || 'Machtiging / contract'}
                    fileId={selected.authorization_file_id}
                    fileName={selected.authorization_file_name}
                    apiBase={API}
                    titleAsFilename
                  />
                </div>
              )}

              {/* Document generation */}
              <div>
                <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-[#f26b1f]" /> Documenten genereren
                </div>

                {/* Missing required fields warning */}
                {(() => {
                  const req = ['naam_geadresseerde','straat_geadresseerde','postcode_geadresseerde','woonplaats_geadresseerde','beschikkingsnummer','datum_beschikking','bedrag','instantie'];
                  const missing = req.filter(k => !selected.boete?.[k] && selected.boete?.[k] !== 0);
                  if (missing.length === 0) return null;
                  return (
                    <div className="mb-3 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-2">
                      Vereiste velden ontbreken voor generatie: {missing.join(', ')}
                    </div>
                  );
                })()}

                <div className="grid md:grid-cols-2 gap-4">
                  {['machtiging','bezwaar'].map(t => {
                    const tpl = templateFor(t);
                    const existing = (selected.generated_documents || []).find(g => g.type === t);
                    const label = t === 'machtiging' ? 'Machtiging' : 'Pro-forma bezwaar';
                    const inputId = `tpl-${t}-${selected.id}`;
                    return (
                      <div key={t} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-[#1e1b4b]">{label}</div>
                          {tpl?.is_default && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">Standaard sjabloon</Badge>}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Sjabloon: {tpl ? <span className="text-slate-700">{tpl.file_name}</span> : <span className="text-rose-600">niet geconfigureerd</span>}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <Button
                            size="sm"
                            className="bg-[#1e1b4b] hover:bg-[#2a2464] text-white"
                            disabled={!tpl || generating === t}
                            onClick={() => generateDocument(t)}
                          >
                            {generating === t ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <FilePlus className="w-3.5 h-3.5 mr-2" />}
                            {existing ? 'Opnieuw genereren' : 'Genereren'}
                          </Button>
                          <input id={inputId} type="file" accept=".docx" className="hidden" onChange={e => e.target.files?.[0] && uploadTemplate(t, e.target.files[0])} />
                          <label htmlFor={inputId}>
                            <Button size="sm" variant="ghost" asChild disabled={uploadingTemplate === t} className="text-slate-500 hover:text-slate-800">
                              <span className="cursor-pointer text-xs">
                                {uploadingTemplate === t ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UploadIcon className="w-3.5 h-3.5 mr-1" />}
                                Sjabloon vervangen
                              </span>
                            </Button>
                          </label>
                        </div>
                        {existing && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="text-xs text-slate-500 mb-2">Laatst gegenereerd: {new Date(existing.generated_at).toLocaleString('nl-NL')}</div>
                            <div className="text-xs text-slate-600 mb-1">Download:</div>
                            <div className="flex flex-wrap gap-2">
                              <a href={`${API}/files/${existing.docx_file_id}?download=1`} download={existing.docx_file_name || 'document.docx'}>
                                <Button size="sm" variant="outline" className="border-[#1e1b4b] text-[#1e1b4b]" data-testid="download-docx-btn"><Download className="w-3.5 h-3.5 mr-2" /> Word (.docx)</Button>
                              </a>
                              <a href={`${API}/files/${existing.pdf_file_id}?download=1`} download={existing.pdf_file_name || 'document.pdf'}>
                                <Button size="sm" variant="outline" className="border-rose-600 text-rose-700" data-testid="download-pdf-btn"><Download className="w-3.5 h-3.5 mr-2" /> PDF</Button>
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Gebruik placeholders in de vorm <code className="bg-slate-100 px-1 rounded">&#123;veldnaam&#125;</code> in je .docx-sjabloon. Beschikbare velden: naam, straat, postcode, woonplaats, email, telefoon, aanhef, beschikkingsnummer, datum_beschikking, kenteken, bedrag, instantie, type, omschrijving_overtreding, tijdstip_gedraging, locatie_gedraging, uiterlijke_datum_bezwaar, naam_geadresseerde, straat_geadresseerde, postcode_geadresseerde, woonplaats_geadresseerde, kenmerk, vandaag, datum_aanmelding, reden.
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="font-semibold text-[#1e1b4b] uppercase text-sm tracking-wide mb-3">Interne notities</div>
                <Textarea rows={4} defaultValue={selected.notes || ''} onBlur={e => patch(selected.id, { notes: e.target.value })} placeholder="Notities worden automatisch opgeslagen bij verlaten veld..." />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 justify-end pt-4 border-t border-slate-200">
                {!selected.archived && !selected.deleted && (
                  <Button variant="outline" onClick={() => { patch(selected.id, { archived: true }); setDetailOpen(false); }}>
                    <Archive className="w-4 h-4 mr-2" /> Archiveren
                  </Button>
                )}
                {(selected.archived || (!selected.archived && !selected.deleted)) && (
                  <Button variant="outline" className="text-rose-600 hover:text-rose-700" onClick={() => { patch(selected.id, { deleted: true }); setDetailOpen(false); }}>
                    <Trash2 className="w-4 h-4 mr-2" /> Verwijderen
                  </Button>
                )}
                {(selected.archived || selected.deleted) && (
                  <Button variant="outline" onClick={() => { patch(selected.id, { archived: false, deleted: false }); setDetailOpen(false); }}>
                    <RotateCcw className="w-4 h-4 mr-2" /> Herstellen
                  </Button>
                )}
                <Button onClick={() => setDetailOpen(false)}><X className="w-4 h-4 mr-2" /> Sluiten</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
